import { Fragment, useEffect, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle, ExternalLink, Network, RefreshCw, Search, Settings, ShieldOff, ShieldAlert, ShieldCheck, History, X,
} from 'lucide-react';
import {
  verbund, type AntragStatus, type VerbundAntrag, type VerbundKnoten, type VerbundKnotenFilter, type VerbundSeite, type VerbundSortierung,
  type VerbundSperre, type VerbundStatus, type VerbundVereinbarungFilter, type VerbundVerlag, type VerbundVerlagFilter, type VerlagStatus,
} from '../api';
import { Blaetterleiste, type Blaettern } from '../components/TabellenSteuerung';
import { useAuth } from '../contexts/AuthContext';
import { useDateFormat } from '../lib/dates';
import { NETZ_NAME_VORGABE, useVerbundStatus } from '../lib/netz';

/**
 * Verwaltung des Verlagsverbunds — die Oberfläche vor der Admin-API seiner
 * Control Plane (CP).
 *
 * Die CP bleibt eigenständig; der PC ruft sie über das Backend
 * (`/api/verbund`). Was jemand darf, kommt fertig vom Backend (`rechte` in der
 * Statusantwort) — Knöpfe, die nicht erlaubt sind, stehen hier nicht. Das ist
 * Sicht, nicht Sicherung: die Routen prüfen dieselbe Tabelle
 * (backend/src/lib/verbund-rechte.ts).
 *
 * Der Name des Verbunds ist ein Arbeitstitel und kommt ebenfalls vom Backend.
 */

type Reiter = 'antraege' | 'verlage' | 'sperren' | 'vereinbarungen';
const REITER: { key: Reiter; label: string }[] = [
  { key: 'antraege', label: 'Anträge' },
  { key: 'verlage', label: 'Verlage & Knoten' },
  { key: 'sperren', label: 'Sperren (Verlauf)' },
  { key: 'vereinbarungen', label: 'Vereinbarungen' },
];

const fehlerText = (e: unknown, sonst = 'Fehlgeschlagen.'): string =>
  (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? sonst;

const KNOPF = 'inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50';
const KNOPF_PRIMAER = 'inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50';
const KARTE = 'rounded-xl border border-slate-200 bg-surface';

// ─── Kennzeichen ────────────────────────────────────────────────────────────

const VERLAG_FARBE: Record<string, string> = {
  aktiv: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  gesperrt: 'border-amber-200 bg-amber-50 text-amber-800',
  ausgeschlossen: 'border-rose-200 bg-rose-50 text-rose-700',
};
const KNOTEN_TEXT: Record<string, [string, string]> = {
  ACTIVE: ['aktiv', 'border-emerald-200 bg-emerald-50 text-emerald-700'],
  SUSPENDED: ['ausgesetzt', 'border-amber-200 bg-amber-50 text-amber-800'],
  EXCLUDED: ['ausgeschlossen', 'border-rose-200 bg-rose-50 text-rose-700'],
};
const ANTRAG_TEXT: Record<AntragStatus, [string, string]> = {
  eingereicht: ['eingereicht — Adressnachweis fehlt', 'border-slate-200 bg-slate-50 text-slate-600'],
  url_geprueft: ['Adresse nachgewiesen — zu prüfen', 'border-amber-200 bg-amber-50 text-amber-800'],
  freigegeben: ['freigegeben — Token abholen', 'border-sky-200 bg-sky-50 text-sky-700'],
  abgelehnt: ['abgelehnt', 'border-rose-200 bg-rose-50 text-rose-700'],
  angemeldet: ['angemeldet', 'border-emerald-200 bg-emerald-50 text-emerald-700'],
};

function Marke({ text, klasse, titel }: { text: string; klasse: string; titel?: string }) {
  return <span title={titel} className={`inline-flex items-center rounded border px-1.5 py-0.5 text-xs ${klasse}`}>{text}</span>;
}
const VerlagMarke = ({ status }: { status: string }) => <Marke text={status} klasse={VERLAG_FARBE[status] ?? 'border-slate-200 text-slate-600'} />;

// ─── Seite ──────────────────────────────────────────────────────────────────

export default function VerbundPage() {
  const statusQ = useVerbundStatus();
  const s = statusQ.data;
  const [params, setParams] = useSearchParams();
  const reiter = (REITER.find((r) => r.key === params.get('reiter'))?.key ?? 'antraege') as Reiter;
  const netzName = s?.netzName ?? NETZ_NAME_VORGABE;

  if (statusQ.isLoading) return <p className="text-sm text-slate-400">wird geladen…</p>;

  return (
    <div className="space-y-4">
      <Kopf status={s} netzName={netzName} neuLaden={() => statusQ.refetch()} laedt={statusQ.isFetching} />

      {s && !s.eingerichtet && <NichtEingerichtet />}
      {s && s.eingerichtet && !s.erreichbar && (
        <p className="flex items-start gap-2 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-800">
          <AlertTriangle size={15} className="mt-0.5 shrink-0" />
          <span>{s.fehler ?? 'Die Control Plane ist nicht erreichbar.'}<span className="block text-xs text-rose-600">{s.cpUrl}</span></span>
        </p>
      )}

      {s?.eingerichtet && (
        <>
          <div className="flex gap-1 border-b border-slate-200">
            {REITER.map((r) => (
              <button
                key={r.key}
                type="button"
                onClick={() => setParams({ reiter: r.key }, { replace: true })}
                className={`-mb-px border-b-2 px-3 py-2 text-sm ${reiter === r.key ? 'border-brand-600 font-medium text-brand-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
              >
                {r.label}
              </button>
            ))}
          </div>
          {reiter === 'antraege' && <Antraege rechte={s.rechte} />}
          {reiter === 'verlage' && <Verlage rechte={s.rechte} />}
          {reiter === 'sperren' && <SperrenVerlauf />}
          {reiter === 'vereinbarungen' && <Vereinbarungen />}
        </>
      )}
    </div>
  );
}

function Kopf({ status, netzName, neuLaden, laedt }: { status?: VerbundStatus; netzName: string; neuLaden: () => void; laedt: boolean }) {
  const qc = useQueryClient();
  const [meldung, setMeldung] = useState<string | null>(null);
  const abgleich = useMutation({
    mutationFn: verbund.abgleich,
    onSuccess: (r) => {
      const fehler = r.filter((x) => x.error).length;
      setMeldung(`Abgleich: ${r.length} Knoten, ${fehler ? `${fehler} mit Fehler` : 'alle ohne Fehler'}.`);
      qc.invalidateQueries({ queryKey: ['verbund'] });
    },
    onError: (e) => setMeldung(fehlerText(e)),
  });
  const seiten = status?.seiten;
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="flex items-center gap-2 text-xl font-semibold text-slate-900">
          <Network size={20} /> {netzName}
        </h1>
        <span className="text-xs text-slate-400">Verlagsverbund · Control Plane</span>
        <button onClick={neuLaden} className="rounded-lg border border-slate-300 p-1.5 hover:bg-slate-50" title="Jetzt neu laden">
          <RefreshCw size={13} className={laedt ? 'animate-spin' : ''} />
        </button>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {status?.rechte.abgleich && status.erreichbar && (
            <button
              className={KNOPF}
              disabled={abgleich.isPending}
              onClick={() => abgleich.mutate()}
              title="Ruft die Kataloge aller aktiven, teilnehmenden Knoten neu ab"
            >
              <RefreshCw size={12} className={abgleich.isPending ? 'animate-spin' : ''} /> Abgleich anstoßen
            </button>
          )}
          {seiten && (
            <>
              <a className={KNOPF} href={seiten.registrierung} target="_blank" rel="noreferrer noopener">Registrierung <ExternalLink size={11} /></a>
              <a className={KNOPF} href={seiten.teilnahmebedingungen} target="_blank" rel="noreferrer noopener">Teilnahmebedingungen <ExternalLink size={11} /></a>
              <a className={KNOPF} href={seiten.anbindung} target="_blank" rel="noreferrer noopener">Anbindung <ExternalLink size={11} /></a>
            </>
          )}
        </div>
      </div>
      {meldung && <p className="text-xs text-slate-600">{meldung}</p>}
    </div>
  );
}

function NichtEingerichtet() {
  const { user } = useAuth();
  return (
    <div className={`${KARTE} p-6`}>
      <p className="mb-2 text-sm text-slate-700">Die Anbindung an die Control Plane ist noch nicht eingerichtet.</p>
      <p className="mb-4 text-sm text-slate-500">
        Adresse und Verwaltungs-Token stehen in den Einstellungen unter „Verlagsverbund“
        ({user?.role === 'ADMIN' ? 'dort einzutragen' : 'eintragen kann eine Person mit der Rolle ADMIN'}).
      </p>
      {user?.role === 'ADMIN' && (
        <Link to="/integrations" className={KNOPF_PRIMAER}><Settings size={14} /> Zu den Einstellungen</Link>
      )}
    </div>
  );
}

// ─── Anträge ────────────────────────────────────────────────────────────────

type AntragFilter = 'offen' | 'alle' | AntragStatus;

function Antraege({ rechte }: { rechte: VerbundStatus['rechte'] }) {
  const [filter, setFilter] = useState<AntragFilter>('offen');
  const [offen, setOffen] = useState<string | null>(null);
  const { dateTime } = useDateFormat();
  const q = useQuery({ queryKey: ['verbund', 'antraege'], queryFn: () => verbund.antraege() });
  const liste = (q.data ?? []).filter((a) =>
    filter === 'alle' ? true : filter === 'offen' ? ['eingereicht', 'url_geprueft', 'freigegeben'].includes(a.status) : a.status === filter);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <label className="text-slate-500">Zeigen:</label>
        <select value={filter} onChange={(e) => setFilter(e.target.value as AntragFilter)} className="rounded-lg border border-slate-300 px-2 py-1 text-sm">
          <option value="offen">offen (eingereicht, nachgewiesen, freigegeben)</option>
          <option value="url_geprueft">nur: zu prüfen</option>
          <option value="angemeldet">angemeldet</option>
          <option value="abgelehnt">abgelehnt</option>
          <option value="alle">alle</option>
        </select>
        <span className="text-xs text-slate-400">{liste.length} von {q.data?.length ?? 0}</span>
      </div>
      {q.error && <p className="text-sm text-rose-700">{fehlerText(q.error)}</p>}
      {q.isLoading ? (
        <div className="h-24 animate-pulse rounded-xl bg-slate-100" />
      ) : liste.length === 0 ? (
        <p className={`${KARTE} p-6 text-sm text-slate-500`}>Keine Anträge in dieser Ansicht.</p>
      ) : (
        <div className={`${KARTE} divide-y divide-slate-100`}>
          {liste.map((a) => (
            <div key={a.id}>
              <button type="button" onClick={() => setOffen(offen === a.id ? null : a.id)} className="flex w-full flex-wrap items-center gap-3 px-4 py-3 text-left hover:bg-slate-50">
                <Marke text={ANTRAG_TEXT[a.status]?.[0] ?? a.status} klasse={ANTRAG_TEXT[a.status]?.[1] ?? ''} />
                <span className="font-medium text-slate-800">{a.verlag}</span>
                <span className="text-xs text-slate-400">{a.land}</span>
                <code className="text-xs text-slate-500">{a.baseUrl}</code>
                <span className="ml-auto text-xs text-slate-400">{dateTime(a.createdAt)}</span>
              </button>
              {offen === a.id && <AntragDetail id={a.id} rechte={rechte} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Feld({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex gap-3 text-sm">
      <span className="w-40 shrink-0 text-slate-500">{label}</span>
      <span className="min-w-0 break-words text-slate-800">{children}</span>
    </div>
  );
}

function AntragDetail({ id, rechte }: { id: string; rechte: VerbundStatus['rechte'] }) {
  const qc = useQueryClient();
  const { dateTime } = useDateFormat();
  const q = useQuery({ queryKey: ['verbund', 'antrag', id], queryFn: () => verbund.antrag(id) });
  const [form, setForm] = useState<'freigeben' | 'ablehnen' | null>(null);
  const [meldung, setMeldung] = useState<{ ok: boolean; text: string } | null>(null);
  const fertig = (text: string) => {
    setMeldung({ ok: true, text });
    setForm(null);
    qc.invalidateQueries({ queryKey: ['verbund'] });
  };
  const neuesToken = useMutation({
    mutationFn: () => verbund.neuesToken(id),
    onSuccess: () => fertig('Neues Anmelde-Token: alte, unbenutzte Tokens sind ungültig, der Antragsteller kann wieder einmal abholen.'),
    onError: (e) => setMeldung({ ok: false, text: fehlerText(e) }),
  });

  const a = q.data;
  if (q.isLoading) return <div className="px-4 pb-4 text-sm text-slate-400">wird geladen…</div>;
  if (!a) return <div className="px-4 pb-4 text-sm text-rose-700">{fehlerText(q.error, 'Antrag nicht gefunden.')}</div>;
  const darfEntscheiden = rechte.antrag_entscheiden;

  return (
    <div className="space-y-3 bg-slate-50/50 px-4 pb-4 pt-1">
      <div className="grid gap-1.5">
        <Feld label="Ansprechperson">{a.ansprechperson} · <a className="text-brand-700 hover:underline" href={`mailto:${a.email}`}>{a.email}</a></Feld>
        <Feld label="Website"><a className="text-brand-700 hover:underline" href={a.website} target="_blank" rel="noreferrer noopener">{a.website}</a></Feld>
        <Feld label="Knoten-Adresse"><code className="text-xs">{a.baseUrl}</code></Feld>
        <Feld label="Beschreibung"><span className="whitespace-pre-wrap">{a.beschreibung}</span></Feld>
        <Feld label="Adressnachweis">{a.urlGeprueftAt ? `nachgewiesen ${dateTime(a.urlGeprueftAt)}` : a.letztePruefungFehler ? `fehlt — ${a.letztePruefungFehler}` : 'steht aus'}</Feld>
        <Feld label="Teilnahmebedingungen">Version {a.bedingungenVersion}</Feld>
        {a.publisherSlug && <Feld label="Verlags-Slug"><code className="text-xs">{a.publisherSlug}</code>{a.residency ? ` · ${a.residency}` : ''}{a.participates ? '' : ' · nimmt nicht teil'}</Feld>}
        {a.entschiedenAt && <Feld label="Entschieden">{dateTime(a.entschiedenAt)} von {a.entschiedenVon ?? '—'}{a.ablehnungsgrund ? ` — ${a.ablehnungsgrund}` : ''}</Feld>}
        {a.tokenAbholbarBis && <Feld label="Token abholbar bis">{dateTime(a.tokenAbholbarBis)}{a.tokenAbgeholtAt ? ` · abgeholt ${dateTime(a.tokenAbgeholtAt)}` : ''}</Feld>}
        {a.angemeldetAt && <Feld label="Angemeldet">{dateTime(a.angemeldetAt)} · Knoten <code className="text-xs">{a.nodeId}</code></Feld>}
        {a.notiz && <Feld label="Notiz (intern)">{a.notiz}</Feld>}
        {(a.tokens?.length ?? 0) > 0 && (
          <Feld label="Anmelde-Tokens">
            {a.tokens!.map((t) => (
              <span key={t.id} className="block text-xs text-slate-600">
                {dateTime(t.createdAt)} · {t.usedAt ? `benutzt ${dateTime(t.usedAt)}` : t.widerrufenAt ? 'ungültig' : `gültig bis ${dateTime(t.expiresAt)}`}
              </span>
            ))}
          </Feld>
        )}
      </div>

      {meldung && <p className={`rounded px-3 py-2 text-sm ${meldung.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-rose-50 text-rose-800'}`}>{meldung.text}</p>}

      {darfEntscheiden && !form && (
        <div className="flex flex-wrap gap-2">
          {a.status === 'url_geprueft' && <button className={KNOPF} onClick={() => { setForm('freigeben'); setMeldung(null); }}><ShieldCheck size={12} /> Freigeben …</button>}
          {['eingereicht', 'url_geprueft', 'freigegeben'].includes(a.status) && <button className={KNOPF} onClick={() => { setForm('ablehnen'); setMeldung(null); }}><X size={12} /> Ablehnen …</button>}
          {a.status === 'freigegeben' && rechte.neues_token && (
            <button className={KNOPF} disabled={neuesToken.isPending} onClick={() => { if (confirm('Neues Anmelde-Token ausstellen? Alte, unbenutzte Tokens werden ungültig.')) neuesToken.mutate(); }}>
              Neues Token
            </button>
          )}
        </div>
      )}
      {form === 'freigeben' && <FreigabeForm antrag={a} abbrechen={() => setForm(null)} fertig={fertig} />}
      {form === 'ablehnen' && <AblehnenForm id={a.id} abbrechen={() => setForm(null)} fertig={fertig} />}
    </div>
  );
}

function FreigabeForm({ antrag, abbrechen, fertig }: { antrag: VerbundAntrag; abbrechen: () => void; fertig: (t: string) => void }) {
  const [slug, setSlug] = useState('');
  const [bestehend, setBestehend] = useState(false);
  const [residency, setResidency] = useState('PREVIEW');
  const [teilnahme, setTeilnahme] = useState(true);
  const [notiz, setNotiz] = useState('');
  const [fehler, setFehler] = useState('');
  const m = useMutation({
    mutationFn: () => verbund.freigeben(antrag.id, {
      ...(slug.trim() ? { publisherSlug: slug.trim(), bestehenderVerlag: bestehend } : {}),
      residency, participates: teilnahme, ...(notiz.trim() ? { notiz: notiz.trim() } : {}),
    }),
    onSuccess: () => fertig(`Freigegeben. Der Antragsteller bekommt eine Mail und holt sein Anmelde-Token auf der Statusseite ab.`),
    onError: (e) => setFehler(fehlerText(e)),
  });
  return (
    <form className={`${KARTE} space-y-3 p-4`} onSubmit={(e) => { e.preventDefault(); setFehler(''); m.mutate(); }}>
      <p className="text-sm font-medium text-slate-800">„{antrag.verlag}“ freigeben</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-sm text-slate-600">Verlags-Slug (leer = aus dem Namen)
          <input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} pattern="[a-z0-9-]*" maxLength={40} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1 font-mono text-sm" />
        </label>
        <label className="text-sm text-slate-600">Datenhaltung im Verbund
          <select value={residency} onChange={(e) => setResidency(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1 text-sm">
            <option value="META_ONLY">nur Metadaten</option>
            <option value="PREVIEW">Metadaten und Vorschau</option>
            <option value="INDEXED_CHUNKS">mit Volltext-Abschnitten</option>
          </select>
        </label>
      </div>
      {slug.trim() && (
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" checked={bestehend} onChange={(e) => setBestehend(e.target.checked)} />
          Slug gehört absichtlich einem bestehenden Verlag (weiterer Knoten desselben Verlags)
        </label>
      )}
      <label className="flex items-center gap-2 text-sm text-slate-600">
        <input type="checkbox" checked={teilnahme} onChange={(e) => setTeilnahme(e.target.checked)} /> nimmt an Suche und Discovery teil
      </label>
      <label className="block text-sm text-slate-600">Notiz (nur intern)
        <textarea value={notiz} onChange={(e) => setNotiz(e.target.value)} rows={2} maxLength={2000} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
      </label>
      {fehler && <p className="rounded bg-rose-50 px-3 py-2 text-sm text-rose-800">{fehler}</p>}
      <div className="flex gap-2">
        <button type="submit" className={KNOPF_PRIMAER} disabled={m.isPending}>Freigeben</button>
        <button type="button" className={KNOPF} onClick={abbrechen}>Abbrechen</button>
      </div>
    </form>
  );
}

function AblehnenForm({ id, abbrechen, fertig }: { id: string; abbrechen: () => void; fertig: (t: string) => void }) {
  const [grund, setGrund] = useState('');
  const [fehler, setFehler] = useState('');
  const m = useMutation({
    mutationFn: () => verbund.ablehnen(id, grund.trim() || undefined),
    onSuccess: () => fertig('Abgelehnt. Der Antragsteller wird per Mail benachrichtigt.'),
    onError: (e) => setFehler(fehlerText(e)),
  });
  return (
    <form className={`${KARTE} space-y-3 p-4`} onSubmit={(e) => { e.preventDefault(); setFehler(''); m.mutate(); }}>
      <p className="text-sm font-medium text-slate-800">Antrag ablehnen</p>
      <label className="block text-sm text-slate-600">Grund (freiwillig — geht an den Antragsteller; Ablehnen ohne Angabe von Gründen ist zulässig)
        <textarea value={grund} onChange={(e) => setGrund(e.target.value)} rows={3} maxLength={2000} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
      </label>
      {fehler && <p className="rounded bg-rose-50 px-3 py-2 text-sm text-rose-800">{fehler}</p>}
      <div className="flex gap-2">
        <button type="submit" className="inline-flex items-center gap-1.5 rounded-lg bg-rose-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50" disabled={m.isPending}>Ablehnen</button>
        <button type="button" className={KNOPF} onClick={abbrechen}>Abbrechen</button>
      </div>
    </form>
  );
}

// ─── Listen: Zustand in der URL ─────────────────────────────────────────────
//
// Suche, Filter, Seite und Seitengröße stehen in der Adresse (`?reiter=verlage
// &q=…&seite=2`): ein Link zeigt dieselbe Ansicht, Zurück im Browser geht
// zurück. Geblättert wird in der Control Plane — der Browser bekommt nur die
// Seite, die er zeigt.

const GROESSEN = [10, 25, 50, 100];
const PRO_SEITE_VORGABE = 25;

/** Ein Wert aus der Adresse, nur wenn er zu den erlaubten gehört. */
function ausListe<T extends string>(wert: string | null, erlaubt: readonly T[]): T | undefined {
  return erlaubt.includes(wert as T) ? (wert as T) : undefined;
}

function useListenZustand() {
  const [params, setParams] = useSearchParams();
  const seite = Math.max(1, Math.floor(Number(params.get('seite'))) || 1);
  const proSeiteRoh = Number(params.get('proSeite'));
  const proSeite = GROESSEN.includes(proSeiteRoh) ? proSeiteRoh : PRO_SEITE_VORGABE;
  /** Werte setzen; leere fallen aus der Adresse. Jede Änderung außer dem Blättern beginnt wieder bei Seite 1. */
  const setze = (werte: Record<string, string | number | undefined>, blaettern = false) => {
    setParams((alt) => {
      const neu = new URLSearchParams(alt);
      for (const [k, v] of Object.entries(werte)) {
        if (v === undefined || v === '') neu.delete(k);
        else neu.set(k, String(v));
      }
      if (!blaettern) neu.delete('seite');
      if (neu.get('seite') === '1') neu.delete('seite');
      if (neu.get('proSeite') === String(PRO_SEITE_VORGABE)) neu.delete('proSeite');
      return neu;
    }, { replace: true });
  };
  return { params, seite, proSeite, setze };
}

/** Freitext, der erst nach einer Tipp-Pause in die Adresse (und damit in die Abfrage) geht. */
function useEntprellteSuche(name: string, setze: (w: Record<string, string | undefined>) => void, params: URLSearchParams, ms = 350) {
  const ausUrl = params.get(name) ?? '';
  const [text, setText] = useState(ausUrl);
  // Adresse von außen geändert (Zurück, Link): Eingabefeld nachziehen
  useEffect(() => {
    setText((t) => (t.trim() === ausUrl ? t : ausUrl));
  }, [ausUrl]);
  useEffect(() => {
    const wert = text.trim();
    if (wert === ausUrl) return undefined;
    const uhr = setTimeout(() => setze({ [name]: wert || undefined }), ms);
    return () => clearTimeout(uhr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, ausUrl]);
  return [text, setText] as const;
}

function blaetternAus<T>(d: VerbundSeite<T> | undefined, seite: number, proSeite: number, setze: (w: Record<string, number>, blaettern?: boolean) => void): Blaettern {
  const gesamt = d?.gesamt ?? 0;
  return {
    seite,
    seiten: Math.max(1, Math.ceil(gesamt / proSeite)),
    setSeite: (n) => setze({ seite: n }, true),
    von: gesamt ? Math.min((seite - 1) * proSeite + 1, gesamt) : 0,
    bis: Math.min(seite * proSeite, gesamt),
    gesamt,
    proSeite,
    setProSeite: (n) => setze({ proSeite: n }),
  };
}

const EINGABE = 'rounded-lg border border-slate-300 px-2 py-1 text-sm';

// ─── Verlage & Knoten ───────────────────────────────────────────────────────

type Dialog = { art: 'gesperrt' | 'ausgeschlossen' | 'aufheben'; verlag: VerbundVerlag } | null;

const VERLAG_STATUS: VerlagStatus[] = ['aktiv', 'gesperrt', 'ausgeschlossen'];
const KNOTEN_FILTER: { wert: VerbundKnotenFilter; label: string }[] = [
  { wert: 'ACTIVE', label: 'mit aktivem Knoten' },
  { wert: 'SUSPENDED', label: 'mit ausgesetztem Knoten' },
  { wert: 'EXCLUDED', label: 'mit ausgeschlossenem Knoten' },
  { wert: 'fehler', label: 'mit Abgleichfehler' },
  { wert: 'ohne', label: 'ohne Knoten' },
];
const SORTIERUNGEN: { wert: VerbundSortierung; label: string }[] = [
  { wert: 'name', label: 'Name A–Z' },
  { wert: 'name_absteigend', label: 'Name Z–A' },
  { wert: 'slug', label: 'Slug' },
  { wert: 'neueste', label: 'neueste zuerst' },
  { wert: 'aelteste', label: 'älteste zuerst' },
];

function Verlage({ rechte }: { rechte: VerbundStatus['rechte'] }) {
  const { params, seite, proSeite, setze } = useListenZustand();
  const [suche, setSuche] = useEntprellteSuche('q', setze, params);
  const filter: VerbundVerlagFilter = {
    seite,
    proSeite,
    q: params.get('q') || undefined,
    status: ausListe(params.get('status'), VERLAG_STATUS),
    knotenStatus: ausListe(params.get('knotenStatus'), KNOTEN_FILTER.map((k) => k.wert)),
    sortierung: ausListe(params.get('sortierung'), SORTIERUNGEN.map((s) => s.wert)),
  };
  const q = useQuery({
    queryKey: ['verbund', 'verlage', filter],
    queryFn: () => verbund.verlage(filter),
    placeholderData: keepPreviousData,
  });
  const [dialog, setDialog] = useState<Dialog>(null);
  const [details, setDetails] = useState<string | null>(null);
  const liste = q.data?.eintraege ?? [];
  const eingeschraenkt = Boolean(filter.q || filter.status || filter.knotenStatus);

  const darfAufheben = (v: VerbundVerlag) =>
    (v.status === 'gesperrt' && rechte.sperre_aufheben) || (v.status === 'ausgeschlossen' && rechte.ausschluss_aufheben);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="relative">
          <Search size={13} className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="search"
            value={suche}
            onChange={(e) => setSuche(e.target.value)}
            maxLength={200}
            placeholder="Name, Slug oder Knoten-Adresse"
            className={`${EINGABE} w-64 pl-7`}
          />
        </span>
        <select value={filter.status ?? ''} onChange={(e) => setze({ status: e.target.value || undefined })} className={EINGABE} aria-label="Verlagsstatus">
          <option value="">alle Status</option>
          {VERLAG_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filter.knotenStatus ?? ''} onChange={(e) => setze({ knotenStatus: e.target.value || undefined })} className={EINGABE} aria-label="Knotenstatus">
          <option value="">alle Knoten</option>
          {KNOTEN_FILTER.map((k) => <option key={k.wert} value={k.wert}>{k.label}</option>)}
        </select>
        <select value={filter.sortierung ?? 'name'} onChange={(e) => setze({ sortierung: e.target.value === 'name' ? undefined : e.target.value })} className={EINGABE} aria-label="Sortierung">
          {SORTIERUNGEN.map((s) => <option key={s.wert} value={s.wert}>{s.label}</option>)}
        </select>
        {eingeschraenkt && (
          <button type="button" className={KNOPF} onClick={() => { setSuche(''); setze({ q: undefined, status: undefined, knotenStatus: undefined }); }}>
            <X size={12} /> Filter zurücksetzen
          </button>
        )}
        <span className="text-xs text-slate-400">
          {q.data ? `${q.data.gesamt} ${q.data.gesamt === 1 ? 'Verlag' : 'Verlage'}${eingeschraenkt ? ' in dieser Ansicht' : ''}` : ''}
          {q.isFetching && !q.isLoading ? ' · lädt…' : ''}
        </span>
      </div>
      {q.error && <p className="text-sm text-rose-700">{fehlerText(q.error)}</p>}
      {q.isLoading && <div className="h-24 animate-pulse rounded-xl bg-slate-100" />}
      {liste.map((v) => (
        <article key={v.id} className={`${KARTE} p-4`}>
          <div className="flex flex-wrap items-center gap-3">
            <VerlagMarke status={v.status} />
            <h2 className="font-semibold text-slate-800">{v.name}</h2>
            <code className="text-xs text-slate-500">{v.slug}</code>
            <span className="text-xs text-slate-400">
              {v.knotenAnzahl} Knoten ·{v.katalogEintraege} Katalogeinträge
              {v.registriert === true ? ' · über Registrierung' : v.registriert === false ? ' · ohne Registrierung' : ''}
            </span>
            <div className="ml-auto flex flex-wrap gap-2">
              <button className={KNOPF} onClick={() => setDetails(details === v.slug ? null : v.slug)}><History size={12} /> Details &amp; Verlauf</button>
              {v.status === 'aktiv' && rechte.sperren && (
                <button className={KNOPF} onClick={() => setDialog({ art: 'gesperrt', verlag: v })}><ShieldAlert size={12} /> Sperren …</button>
              )}
              {v.status !== 'ausgeschlossen' && rechte.ausschliessen && (
                <button className={`${KNOPF} border-rose-300 text-rose-700 hover:bg-rose-50`} onClick={() => setDialog({ art: 'ausgeschlossen', verlag: v })}><ShieldOff size={12} /> Ausschließen …</button>
              )}
              {darfAufheben(v) && (
                <button className={KNOPF} onClick={() => setDialog({ art: 'aufheben', verlag: v })}><ShieldCheck size={12} /> {v.status === 'ausgeschlossen' ? 'Ausschluss aufheben …' : 'Sperre aufheben …'}</button>
              )}
            </div>
          </div>
          <KnotenTabelle knoten={v.knoten} darfAbgleich={rechte.abgleich} />
          {details === v.slug && <VerlagDetails slug={v.slug} />}
        </article>
      ))}
      {q.data && liste.length === 0 && (
        <p className={`${KARTE} p-6 text-sm text-slate-500`}>
          {seite > 1 && q.data.gesamt > 0 ? 'Diese Seite ist leer — zurück zur ersten Seite.' : 'Keine Verlage in dieser Ansicht.'}
        </p>
      )}
      {q.data && <Blaetterleiste blaettern={blaetternAus(q.data, seite, proSeite, setze)} groessen={GROESSEN} />}
      {dialog && <SperrDialog dialog={dialog} schliessen={() => setDialog(null)} />}
    </div>
  );
}

function KnotenTabelle({ knoten, darfAbgleich }: { knoten: VerbundKnoten[]; darfAbgleich?: boolean }) {
  const { relative, dateTime } = useDateFormat();
  const qc = useQueryClient();
  const [meldung, setMeldung] = useState<Record<string, string>>({});
  const abgleich = useMutation({
    mutationFn: (id: string) => verbund.knotenAbgleich(id),
    onSuccess: (_r, id) => { setMeldung((m) => ({ ...m, [id]: 'abgeglichen' })); qc.invalidateQueries({ queryKey: ['verbund'] }); },
    onError: (e, id) => setMeldung((m) => ({ ...m, [id]: fehlerText(e) })),
  });
  if (!knoten.length) return <p className="mt-2 text-xs italic text-slate-400">Kein Knoten angemeldet.</p>;
  return (
    <div className="mt-3 overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-slate-400">
            <th className="py-1 pr-3 font-medium">Knoten</th>
            <th className="py-1 pr-3 font-medium">Status</th>
            <th className="py-1 pr-3 font-medium">Letzter Abgleich</th>
            <th className="py-1 pr-3 font-medium">Fehler</th>
            <th className="py-1 pr-3 text-right font-medium">Einträge</th>
            <th className="py-1 pr-3 font-medium">Instanz im PC</th>
            {darfAbgleich && <th />}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {knoten.map((k) => {
            const [text, klasse] = KNOTEN_TEXT[k.status] ?? [k.status, 'border-slate-200 text-slate-600'];
            return (
              <tr key={k.id}>
                <td className="py-1.5 pr-3">
                  <a href={k.baseUrl} target="_blank" rel="noreferrer noopener" className="font-mono text-xs text-brand-700 hover:underline">{k.baseUrl}</a>
                  <span className="block text-[11px] text-slate-400">{k.id}{k.residency ? ` · ${k.residency}` : ''}{k.participates ? '' : ' · nimmt nicht teil'}</span>
                </td>
                <td className="py-1.5 pr-3"><Marke text={text} klasse={klasse} /></td>
                <td className="py-1.5 pr-3 text-xs text-slate-600" title={dateTime(k.letzterAbgleich)}>{relative(k.letzterAbgleich)}</td>
                <td className="py-1.5 pr-3 text-xs">
                  {k.fehlerKlasse
                    ? <span className="text-amber-700" title={k.abgleichFehler ?? 'Volle Meldung unter „Details & Verlauf“'}>{k.fehlerKlasse}</span>
                    : <span className="text-slate-300">—</span>}
                </td>
                <td className="py-1.5 pr-3 text-right text-xs text-slate-700">{k.eintraege ?? '—'}</td>
                <td className="py-1.5 pr-3 text-xs">
                  {k.instanz ? <Link to={`/instances/${k.instanz.id}`} className="text-brand-700 hover:underline">{k.instanz.name}</Link> : <span className="text-slate-300">—</span>}
                </td>
                {darfAbgleich && (
                  <td className="py-1.5 text-right">
                    {k.status === 'ACTIVE' && (
                      <button className={KNOPF} disabled={abgleich.isPending} onClick={() => abgleich.mutate(k.id)} title={meldung[k.id] ?? 'Katalog dieses Knotens jetzt abrufen'}>
                        <RefreshCw size={11} />
                      </button>
                    )}
                    {meldung[k.id] && <span className="ml-1 text-[11px] text-slate-500">{meldung[k.id]}</span>}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Detail eines Verlags (Rohtext der Abgleichfehler, Registrierungen) und sein Sperrverlauf. */
function VerlagDetails({ slug }: { slug: string }) {
  const { dateTime } = useDateFormat();
  const d = useQuery({ queryKey: ['verbund', 'verlag', slug], queryFn: () => verbund.verlag(slug) });
  const q = useQuery({ queryKey: ['verbund', 'verlauf', slug], queryFn: () => verbund.verlauf(slug) });
  if (d.isLoading || q.isLoading) return <p className="mt-3 text-xs text-slate-400">wird geladen…</p>;
  const fehlerhaft = (d.data?.knoten ?? []).filter((k) => k.abgleichFehler);
  const l = q.data?.sperren ?? [];
  return (
    <div className="mt-3 space-y-3 border-t border-slate-100 pt-3">
      {d.error && <p className="text-xs text-rose-700">{fehlerText(d.error)}</p>}
      {fehlerhaft.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-slate-600">Letzter Abgleichfehler (volle Meldung)</p>
          {fehlerhaft.map((k) => (
            <p key={k.id} className="text-xs text-slate-700">
              <code className="text-slate-500">{k.baseUrl}</code> · <span className="text-amber-700">{k.fehlerKlasse}</span>
              <span className="mt-0.5 block whitespace-pre-wrap break-words rounded bg-slate-50 px-2 py-1 font-mono text-[11px] text-slate-600">{k.abgleichFehler}</span>
            </p>
          ))}
        </div>
      )}
      {(d.data?.registrierungen.length ?? 0) > 0 && (
        <div className="space-y-0.5">
          <p className="text-xs font-medium text-slate-600">Registrierungen</p>
          {d.data!.registrierungen.map((r) => (
            <p key={r.id} className="text-xs text-slate-600">
              <Marke text={ANTRAG_TEXT[r.status]?.[0] ?? r.status} klasse={ANTRAG_TEXT[r.status]?.[1] ?? ''} />{' '}
              <code className="text-slate-500">{r.baseUrl}</code> · {dateTime(r.createdAt)}
            </p>
          ))}
        </div>
      )}
      {q.error ? <p className="text-xs text-rose-700">{fehlerText(q.error)}</p>
        : l.length === 0 ? <p className="text-xs text-slate-400">Noch nie gesperrt.</p> : <SperrListe sperren={l} mitVerlag={false} />}
    </div>
  );
}

const MIN_GRUND = 10;

function SperrDialog({ dialog, schliessen }: { dialog: NonNullable<Dialog>; schliessen: () => void }) {
  const qc = useQueryClient();
  const v = dialog.verlag;
  const [grund, setGrund] = useState('');
  const [domains, setDomains] = useState('');
  const [bestaetigt, setBestaetigt] = useState('');
  const [fehler, setFehler] = useState('');
  const ausschluss = dialog.art === 'ausgeschlossen';
  const m = useMutation({
    mutationFn: () => dialog.art === 'aufheben'
      ? verbund.aufheben(v.slug, grund.trim())
      : verbund.sperre(v.slug, {
          art: dialog.art, grund: grund.trim(),
          ...(ausschluss && domains.trim() ? { domains: domains.split(/[\s,;]+/).filter(Boolean) } : {}),
        }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['verbund'] }); schliessen(); },
    onError: (e) => setFehler(fehlerText(e)),
  });
  const grundOk = grund.trim().length >= MIN_GRUND;
  const bereit = grundOk && (!ausschluss || bestaetigt.trim() === v.slug);
  const titel = dialog.art === 'aufheben'
    ? (v.status === 'ausgeschlossen' ? 'Ausschluss aufheben' : 'Sperre aufheben')
    : ausschluss ? 'Endgültig ausschließen' : 'Vorläufig sperren';

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:p-8">
      <form
        className="w-full max-w-lg space-y-4 rounded-xl bg-surface p-5 shadow-xl"
        onSubmit={(e) => { e.preventDefault(); setFehler(''); if (bereit) m.mutate(); }}
      >
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-lg font-semibold text-slate-900">{titel}: {v.name}</h2>
          <button type="button" onClick={schliessen} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>

        {dialog.art === 'gesperrt' && (
          <p className="text-sm text-slate-600">
            Vorläufig und umkehrbar: die aktiven Knoten des Verlags werden ausgesetzt, Vereinbarungen ruhen,
            offene Anmelde-Tokens werden ungültig. Beim Aufheben kommen Knoten und Vereinbarungen zurück.
            Der Verlag wird per Mail benachrichtigt, mit dieser Begründung.
          </p>
        )}
        {ausschluss && (
          <div className="space-y-2 rounded-lg border border-rose-300 bg-rose-50 p-3 text-sm text-rose-900">
            <p className="flex items-center gap-2 font-semibold"><AlertTriangle size={16} /> Endgültig — das lässt sich nicht zurückdrehen.</p>
            <ul className="list-disc space-y-1 pl-5">
              <li>Alle Knoten des Verlags werden ausgeschlossen.</li>
              <li>Der <strong>Katalog des Verlags wird aus dem Verbund gelöscht</strong>.</li>
              <li>Alle Vereinbarungen werden beendet, offene Vorschläge verfallen.</li>
              <li>Domains und Name des Verlags sind für eine <strong>Neuregistrierung gesperrt</strong>.</li>
            </ul>
            <p>Ein späteres Aufheben gibt nur die Neuregistrierung wieder frei — Knoten, Katalog und Vereinbarungen kommen nicht zurück.</p>
          </div>
        )}
        {dialog.art === 'aufheben' && (
          <p className="text-sm text-slate-600">
            {v.status === 'ausgeschlossen'
              ? 'Gibt Domains und Namen für eine neue Registrierung frei. Knoten bleiben ausgeschlossen, Vereinbarungen beendet — eine Rückkehr geht nur über eine neue Registrierung.'
              : 'Die von der Sperre ausgesetzten Knoten werden wieder aktiv, ausgesetzte Vereinbarungen leben wieder auf (sofern der Partner aktiv ist). Offene Anmelde-Tokens bleiben ungültig.'}
          </p>
        )}

        <label className="block text-sm text-slate-700">
          Begründung (Pflicht, mindestens {MIN_GRUND} Zeichen{dialog.art !== 'aufheben' ? ' — geht an den Verlag' : ''})
          <textarea autoFocus required value={grund} onChange={(e) => setGrund(e.target.value)} rows={4} maxLength={4000} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
          <span className={`text-xs ${grundOk ? 'text-slate-400' : 'text-amber-700'}`}>{grund.trim().length} Zeichen</span>
        </label>

        {ausschluss && (
          <>
            <label className="block text-sm text-slate-700">
              Weitere Domains, die gesperrt werden sollen (freiwillig; Knoten-Adressen und Website sind schon dabei)
              <input value={domains} onChange={(e) => setDomains(e.target.value)} placeholder="weitere-domain.de andere.de" className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1 font-mono text-sm" />
            </label>
            <label className="block text-sm text-slate-700">
              Zur Bestätigung den Slug <code className="font-mono">{v.slug}</code> eintippen
              <input value={bestaetigt} onChange={(e) => setBestaetigt(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1 font-mono text-sm" />
            </label>
          </>
        )}

        {fehler && <p className="rounded bg-rose-50 px-3 py-2 text-sm text-rose-800">{fehler}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" className={KNOPF} onClick={schliessen}>Abbrechen</button>
          <button
            type="submit"
            disabled={!bereit || m.isPending}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50 ${ausschluss ? 'bg-rose-600 hover:bg-rose-700' : dialog.art === 'gesperrt' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-brand-600 hover:bg-brand-700'}`}
          >
            {titel}
          </button>
        </div>
      </form>
    </div>
  );
}

// ─── Sperren (Verlauf) ──────────────────────────────────────────────────────

function SperrenVerlauf() {
  const [alle, setAlle] = useState(false);
  const q = useQuery({ queryKey: ['verbund', 'sperren', alle], queryFn: () => verbund.sperren(alle) });
  return (
    <div className="space-y-3">
      <label className="flex items-center gap-2 text-sm text-slate-600">
        <input type="checkbox" checked={alle} onChange={(e) => setAlle(e.target.checked)} /> auch aufgehobene zeigen
      </label>
      {q.error && <p className="text-sm text-rose-700">{fehlerText(q.error)}</p>}
      {q.isLoading ? <div className="h-24 animate-pulse rounded-xl bg-slate-100" /> : (q.data ?? []).length === 0 ? (
        <p className={`${KARTE} p-6 text-sm text-slate-500`}>{alle ? 'Noch keine Sperren.' : 'Keine laufende Sperre.'}</p>
      ) : (
        <div className={`${KARTE} p-4`}><SperrListe sperren={q.data ?? []} mitVerlag /></div>
      )}
      <p className="text-xs text-slate-400">
        Der Verlauf steht in der Control Plane; jede Aktion aus dem PC steht zusätzlich im Audit des PC.
      </p>
    </div>
  );
}

function SperrListe({ sperren, mitVerlag }: { sperren: VerbundSperre[]; mitVerlag: boolean }) {
  const { dateTime } = useDateFormat();
  return (
    <ul className="space-y-3">
      {sperren.map((s) => (
        <li key={s.id} className="text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <VerlagMarke status={s.art} />
            {mitVerlag && <span className="font-medium text-slate-800">{s.verlag?.name ?? s.publisherId}</span>}
            <span className="text-xs text-slate-500">{dateTime(s.createdAt)} · {s.von}</span>
            {s.aufgehobenAt
              ? <span className="text-xs text-emerald-700">aufgehoben {dateTime(s.aufgehobenAt)} · {s.aufgehobenVon}</span>
              : <span className="text-xs font-medium text-amber-700">läuft</span>}
          </div>
          <p className="mt-0.5 whitespace-pre-wrap text-slate-700">{s.grund}</p>
          {s.aufhebungsGrund && <p className="mt-0.5 whitespace-pre-wrap text-xs text-slate-500">Aufgehoben: {s.aufhebungsGrund}</p>}
          <p className="text-[11px] text-slate-400">
            {s.nodeIds.length} Knoten · {s.agreementIds.length} Vereinbarungen · {s.tokenIds.length} Tokens
            {s.domains.length ? ` · gesperrte Domains: ${s.domains.join(', ')}` : ''}
          </p>
        </li>
      ))}
    </ul>
  );
}

// ─── Vereinbarungen ─────────────────────────────────────────────────────────

const VEREINBARUNG_STATUS = ['ACTIVE', 'SUSPENDED', 'ENDED', 'SUPERSEDED'] as const;

const richtung = (r: { visible?: boolean; referralPct?: number } | null) =>
  r ? `${r.visible ? 'sichtbar' : 'nicht sichtbar'} · ${r.referralPct ?? 0} %` : '—';

function Vereinbarungen() {
  const { date } = useDateFormat();
  const { params, seite, proSeite, setze } = useListenZustand();
  const [verlagText, setVerlagText] = useEntprellteSuche('verlag', setze, params);
  const verlagRoh = params.get('verlag') ?? '';
  const verlagGueltig = /^[a-z0-9-]{1,60}$/.test(verlagRoh);
  const filter: VerbundVereinbarungFilter = {
    seite,
    proSeite,
    verlag: verlagGueltig ? verlagRoh : undefined,
    status: ausListe(params.get('status'), VEREINBARUNG_STATUS),
  };
  const q = useQuery({
    queryKey: ['verbund', 'vereinbarungen', filter],
    queryFn: () => verbund.vereinbarungen(filter),
    placeholderData: keepPreviousData,
  });
  const [offen, setOffen] = useState<string | null>(null);
  const l = q.data?.eintraege ?? [];
  const verlagWaehlen = (slug: string) => { setVerlagText(slug); setze({ verlag: slug }); };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="relative">
          <Search size={13} className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="search"
            value={verlagText}
            onChange={(e) => setVerlagText(e.target.value.toLowerCase())}
            maxLength={60}
            placeholder="Verlags-Slug"
            className={`${EINGABE} w-48 pl-7 font-mono`}
          />
        </span>
        <select value={filter.status ?? ''} onChange={(e) => setze({ status: e.target.value || undefined })} className={EINGABE} aria-label="Status">
          <option value="">alle Status</option>
          {VEREINBARUNG_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        {(filter.verlag || filter.status) && (
          <button type="button" className={KNOPF} onClick={() => { setVerlagText(''); setze({ verlag: undefined, status: undefined }); }}>
            <X size={12} /> Filter zurücksetzen
          </button>
        )}
        <span className="text-xs text-slate-400">
          {q.data ? `${q.data.gesamt} ${q.data.gesamt === 1 ? 'Vereinbarung' : 'Vereinbarungen'} (je Paar die aktuelle Version)` : ''}
          {q.isFetching && !q.isLoading ? ' · lädt…' : ''}
        </span>
      </div>
      {verlagRoh && !verlagGueltig && <p className="text-xs text-amber-700">Ein Verlags-Slug besteht nur aus a–z, 0–9 und Bindestrich.</p>}
      {q.error && <p className="text-sm text-rose-700">{fehlerText(q.error)}</p>}
      {q.isLoading ? <div className="h-24 animate-pulse rounded-xl bg-slate-100" /> : l.length === 0 ? (
        <p className={`${KARTE} p-6 text-sm text-slate-500`}>Keine Vereinbarungen in dieser Ansicht.</p>
      ) : (
        <div className={`${KARTE} overflow-x-auto p-4`}>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-400">
                <th className="py-1 pr-3 font-medium">Verlage</th>
                <th className="py-1 pr-3 font-medium">Version</th>
                <th className="py-1 pr-3 font-medium">Status</th>
                <th className="py-1 pr-3 font-medium">A → B</th>
                <th className="py-1 pr-3 font-medium">B → A</th>
                <th className="py-1 pr-3 font-medium">Verrechnung</th>
                <th className="py-1 pr-3 font-medium">gültig</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {l.map((a) => (
                <Fragment key={a.id}>
                  <tr>
                    <td className="py-1.5 pr-3">
                      <button type="button" className="hover:underline" title={`Nur Vereinbarungen von ${a.verlagA.slug}`} onClick={() => verlagWaehlen(a.verlagA.slug)}>{a.verlagA.name}</button>
                      {' ↔ '}
                      <button type="button" className="hover:underline" title={`Nur Vereinbarungen von ${a.verlagB.slug}`} onClick={() => verlagWaehlen(a.verlagB.slug)}>{a.verlagB.name}</button>
                    </td>
                    <td className="py-1.5 pr-3 text-xs">
                      v{a.version}
                      {a.versionen > 1 && (
                        <button type="button" className="ml-1 text-brand-700 hover:underline" onClick={() => setOffen(offen === a.id ? null : a.id)}>
                          {offen === a.id ? 'Verlauf zu' : `${a.versionen} Versionen`}
                        </button>
                      )}
                    </td>
                    <td className="py-1.5 pr-3 text-xs">{a.status}</td>
                    <td className="py-1.5 pr-3 text-xs">{richtung(a.aToB)}</td>
                    <td className="py-1.5 pr-3 text-xs">{richtung(a.bToA)}</td>
                    <td className="py-1.5 pr-3 text-xs">{a.nettingMode ?? '—'}</td>
                    <td className="py-1.5 pr-3 text-xs">{date(a.validFrom)}{a.validUntil ? ` – ${date(a.validUntil)}` : ''}</td>
                  </tr>
                  {offen === a.id && (
                    <tr><td colSpan={7} className="bg-slate-50/50 px-3 py-2"><VereinbarungVerlauf id={a.id} /></td></tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {q.data && <Blaetterleiste blaettern={blaetternAus(q.data, seite, proSeite, setze)} groessen={GROESSEN} />}
      <p className="text-xs text-slate-400">
        Nur lesend. Die Control Plane bietet zum Beenden einer einzelnen Vereinbarung keine Verwaltungsroute an;
        beendet werden Vereinbarungen dort durch einen Ausschluss, ausgesetzt durch eine Sperre.
      </p>
    </div>
  );
}

function VereinbarungVerlauf({ id }: { id: string }) {
  const { date } = useDateFormat();
  const q = useQuery({ queryKey: ['verbund', 'vereinbarung', id], queryFn: () => verbund.vereinbarungVerlauf(id) });
  if (q.isLoading) return <p className="text-xs text-slate-400">wird geladen…</p>;
  if (q.error) return <p className="text-xs text-rose-700">{fehlerText(q.error)}</p>;
  return (
    <ul className="space-y-0.5 text-xs text-slate-600">
      {(q.data ?? []).map((v) => (
        <li key={v.id}>
          v{v.version} · {v.status} · A → B {richtung(v.aToB)} · B → A {richtung(v.bToA)} · {v.nettingMode ?? '—'} ·{' '}
          {date(v.validFrom)}{v.validUntil ? ` – ${date(v.validUntil)}` : ''}
        </li>
      ))}
    </ul>
  );
}
